import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Select, TimePicker, InputNumber } from "antd";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import {
  TIME_FORMAT,
  TIME_FORMAT_12HM,
} from "variables/constants";
import {
  getEmployeeScheduleTemplateDetails,
  doEmployeeScheduleTemplateCreate,
  doEmployeeScheduleTemplateUpdate,
} from "../../../../redux/employeescheduletemplate/employeeScheduleTemplateState";
import { formatDateTimeOrNull } from "utils";
import SwitchField from "components/fields/SwitchField";
import CrudButtons from "components/button/crudbuttons";
import dayjs from "dayjs";

const EmployeeScheduleTemplateForm = ({
  onCancel,
  ...props
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { mode, recordId } = props;

  const recordData = useSelector((state) => state.employeeScheduleTemplate.employeeScheduleTemplateDetails?.data);
  
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const { RangePicker } = TimePicker;

  const initialSchedules = daysOfWeek.map((_, index) => ({
    esd_day: index,
    esd_is_flexible: false,
    esd_flexible_time: null,
  }));

  useEffect(() => {
    dispatch(getEmployeeScheduleTemplateDetails(recordId));
  }, [dispatch, recordId]);

  useEffect(() => {
    if (recordData) {
      form.setFieldsValue({
        est_id: recordData.est_id,
        est_title: recordData.est_title,
        schedules: recordData.schedules?.length
          ? recordData.schedules.map((schedule) => ({
              ...schedule,
              time_range:
                schedule.esd_time_in && schedule.esd_time_out
                  ? [
                      dayjs(schedule.esd_time_in, TIME_FORMAT),
                      dayjs(schedule.esd_time_out, TIME_FORMAT),
                    ]
                  : null,
            }))
          : initialSchedules,
      });
    }
  }, [recordData, form]);

  const onSubmit = (formData) => {
    const schedules = formData.schedules.map((schedule) => ({
      ...schedule,

      esd_time_in: schedule.time_range?.[0]
        ? formatDateTimeOrNull(schedule.time_range[0], TIME_FORMAT)
        : null,

      esd_time_out: schedule.time_range?.[1]
        ? formatDateTimeOrNull(schedule.time_range[1], TIME_FORMAT)
        : null,

      esd_is_flexible: Number(Boolean(schedule.esd_is_flexible)),
    }));

    const data = {
      ...formData,
      schedules,
    };

    if (recordData?.est_id > 0) {
      dispatch(doEmployeeScheduleTemplateUpdate(data));
    } else {
      dispatch(doEmployeeScheduleTemplateCreate(data));
    }
  };

  const getScheduleTypeLabel = (schedule) => {
    const option = recordData?.scheduleTypeOptions?.find(
      (option) => Number(option.value) === Number(schedule?.esd_type_id)
    );

    return option?.label || "-";
  };

  return (
    <div className="p-2 border rounded-xl mt-6 mx-2">
      <Form
        className={mode !== "view" ? "form" : ""}
        layout="vertical"
        form={form}
        onFinish={onSubmit}
      >
        <FormItem name="est_id" hidden>
          <Input type="hidden" />
        </FormItem>

        {mode !== "view" ? (
          <FormItem
            name="est_title"
            label="Template Name"
            rules={[requiredField()]}
            className="py-1"
          >
            <Input
              id="est_title"
              className="input-field"
              placeholder="Template name"
              maxLength={30}
            />
          </FormItem>
        ) : (
          <div className="p-2 mb-2">
            <h2 className="text-sm font-medium muted-text">
              Template Name
            </h2>
            <label className="text-base label-value title-text font-medium">
              {recordData?.est_title || "-"}
            </label>
          </div>
        )}
        {mode === "view" ? (
          recordData?.schedules?.map((schedule, index) => (
            <div
              key={schedule.esd_id ?? index}
              className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-2 border rounded-2xl p-2 w-full"
            >
              <div className="p-2 md:col-span-2">
                <h2 className="text-sm font-medium muted-text">
                  {daysOfWeek[index]}
                </h2>
                <label className="text-base label-value title-text font-medium">
                  {getScheduleTypeLabel(schedule)}
                </label>
              </div>

              <div className="p-2 md:col-span-2">
                <h2 className="text-sm font-medium muted-text">
                  Time In / Time Out
                </h2>
                <label className="text-base label-value title-text font-medium">
                  {schedule?.esd_time_in && schedule?.esd_time_out
                    ? `${dayjs(
                        schedule.esd_time_in,
                        TIME_FORMAT
                      ).format(TIME_FORMAT_12HM)} - ${dayjs(
                        schedule.esd_time_out,
                        TIME_FORMAT
                      ).format(TIME_FORMAT_12HM)}`
                    : "-"}
                </label>
              </div>

              <div className="flex gap-4 md:col-span-3">
                <div className="p-2">
                  <h2 className="text-sm font-medium muted-text">
                    Is Flexible
                  </h2>
                  <label className="text-base label-value title-text font-medium">
                    {schedule?.esd_is_flexible ? "Yes" : "No"}
                  </label>
                </div>

                {schedule?.esd_is_flexible ? (
                  <div className="p-2">
                    <h2 className="text-sm font-medium muted-text">
                      Flexible Time
                    </h2>
                    <label className="text-base label-value title-text font-medium">
                      {schedule?.esd_flexible_time ?? "-"}
                    </label>
                  </div>
                ) : null}
              </div>
            </div>
          ))
          ) : (
          <Form.List name="schedules">
            {(fields) =>
              fields.map((field, index) => {
                const schedule = recordData?.schedules?.[index];

                return (
                  <div
                    key={field.key}
                    className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-2 border rounded-2xl p-2 w-full"
                  >
                    <FormItem
                      {...field}
                      name={[index, "esd_day"]}
                      hidden
                    >
                      <Input type="hidden" />
                    </FormItem>

                    {mode !== "view" ? (
                      <FormItem
                        extra="md:col-span-2"
                        {...field}
                        name={[field.name, "esd_type_id"]}
                        label={daysOfWeek[index]}
                        rules={[requiredField()]}
                      >
                        <Select
                          id={`esd_type_id_${index}`}
                          className="input-field"
                          showSearch
                          allowClear
                          options={recordData?.scheduleTypeOptions}
                        />
                      </FormItem>
                    ) : (
                      <div className="p-2 md:col-span-2">
                        <h2 className="text-sm font-medium muted-text">
                          {daysOfWeek[index]}
                        </h2>
                        <label className="text-base label-value title-text font-medium">
                          {getScheduleTypeLabel(schedule)}
                        </label>
                      </div>
                    )}

                    {mode !== "view" ? (
                      <FormItem
                        extra="md:col-span-2"
                        {...field}
                        name={[field.name, "time_range"]}
                        label="Time In / Time Out"
                      >
                        <RangePicker
                          format={TIME_FORMAT_12HM}
                          className="input-field"
                          style={{ width: "100%" }}
                          allowClear
                        />
                      </FormItem>
                    ) : (
                      <div className="p-2 md:col-span-2">
                        <h2 className="text-sm font-medium muted-text">
                          Time In / Time Out
                        </h2>
                        <label className="text-base label-value title-text font-medium">
                          {schedule?.esd_time_in && schedule?.esd_time_out
                            ? `${dayjs(
                                schedule.esd_time_in,
                                TIME_FORMAT
                              ).format(TIME_FORMAT_12HM)} - ${dayjs(
                                schedule.esd_time_out,
                                TIME_FORMAT
                              ).format(TIME_FORMAT_12HM)}`
                            : "-"}
                        </label>
                      </div>
                    )}

                    {mode !== "view" ? (
                      <div className="flex gap-2 md:col-span-3">
                        <FormItem
                          {...field}
                          valuePropName="checked"
                          name={[field.name, "esd_is_flexible"]}
                          label="Is Flexible"
                        >
                          <SwitchField
                            id={`esd_is_flexible_${index}`}
                            type="text"
                            labelYes="Yes"
                            labelNo="No"
                          />
                        </FormItem>

                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.schedules?.[index]?.esd_is_flexible !==
                            currentValues.schedules?.[index]?.esd_is_flexible
                          }
                        >
                          {({ getFieldValue }) => {
                            const isFlexible = getFieldValue([
                              "schedules",
                              index,
                              "esd_is_flexible",
                            ]);

                            return isFlexible ? (
                              <FormItem
                                {...field}
                                name={[field.name, "esd_flexible_time"]}
                                label="Flexible Time"
                              >
                                <InputNumber
                                  className="input-field"
                                  placeholder="Flexible time"
                                  min={1}
                                />
                              </FormItem>
                            ) : null;
                          }}
                        </Form.Item>
                      </div>
                    ) : (
                    <div className="flex gap-4 md:col-span-3">
                      <div className="p-2">
                        <h2 className="text-sm font-medium muted-text">
                          Is Flexible
                        </h2>
                        <label className="text-base label-value title-text font-medium">
                          {schedule?.esd_is_flexible ? "Yes" : "No"}
                        </label>
                      </div>

                      {schedule?.esd_is_flexible ? (
                        <div className="p-2">
                          <h2 className="text-sm font-medium muted-text">
                            Flexible Time
                          </h2>
                          <label className="text-base label-value title-text font-medium">
                            {schedule?.esd_flexible_time ?? "-"}
                          </label>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })
          }
        </Form.List>
        )}
        <div className="w-full flex justify-end">
          {mode === "edit" && (
            <CrudButtons
              mode={mode}
              recordId={recordData?.est_id ?? 0}
              isModal={true}
              modalId={
                recordData?.est_id > 0
                  ? "edit_employee_schedule_template"
                  : "add_employee_schedule_template"
              }
              doHideModal={onCancel}
              cancelLabel="Cancel"
            />
          )}
        </div>
      </Form>
    </div>
  );
};

export default EmployeeScheduleTemplateForm;